// Pure date-math helpers for the Project Pulse timeline. No React, no fetch.

const WORK_HOURS_PER_DAY = 8;
const DEFAULT_ESTIMATE_HOURS = 2; // mirrors POST /tasks/bulk default when 0 hours provided

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function isWorkingDay(d) {
  const day = d.getDay(); // 0 = Sun, 6 = Sat
  return day !== 0 && day !== 6;
}

function previousWorkingDay(d) {
  let prev = addDays(d, -1);
  while (!isWorkingDay(prev)) prev = addDays(prev, -1);
  return prev;
}

/**
 * Computes a task's bar span for the Project Pulse timeline.
 *
 * The bar's right edge is anchored to the end of `customer_due_date`. Total
 * effort (estimated_hours + estimated_minutes) is consumed backward in
 * WORK_HOURS_PER_DAY (8h) blocks across working days only (Mon-Fri), so the
 * bar visually skips weekend effort. A fractional remainder on the earliest
 * day touched is expressed as `startFraction` — the fraction of that day's
 * column (measured from its start) at which the bar begins, so the bar
 * always ends flush with that day's right edge.
 *
 * Example: due date D, 12h estimated -> 8h consumes all of D (fraction 0,
 * i.e. starts at D's start), remaining 4h consumes the previous working day
 * starting at its midpoint (startFraction 0.5).
 *
 * Returns null when there's no due date to anchor to (task is excluded from
 * the timeline in that case).
 */
export function computeTaskBarSpan(task) {
  const dueDateRaw = task?.customer_due_date;
  if (!dueDateRaw) return null;

  const dueDate = startOfDay(dueDateRaw);
  if (Number.isNaN(dueDate.getTime())) return null;

  const hours = Number(task?.estimated_hours) || 0;
  const minutes = Number(task?.estimated_minutes) || 0;
  let totalHours = hours + minutes / 60;
  if (totalHours <= 0) totalHours = DEFAULT_ESTIMATE_HOURS;

  const endDate = dueDate; // bar always ends at (right edge of) the due date's column
  let cursorDay = isWorkingDay(dueDate) ? dueDate : previousWorkingDay(addDays(dueDate, 1));
  let remainingHours = totalHours;
  let startDate = cursorDay;
  let startFraction = 0;

  // Consume the due-date day itself first (if it's a working day), then walk
  // backward one working day at a time.
  while (remainingHours > 0) {
    if (remainingHours >= WORK_HOURS_PER_DAY) {
      remainingHours -= WORK_HOURS_PER_DAY;
      startDate = cursorDay;
      startFraction = 0;
      if (remainingHours > 0) cursorDay = previousWorkingDay(cursorDay);
    } else {
      // Fractional day: bar starts partway through cursorDay, ending flush
      // with cursorDay's end.
      startDate = cursorDay;
      startFraction = 1 - remainingHours / WORK_HOURS_PER_DAY;
      remainingHours = 0;
    }
  }

  return { startDate, startFraction, endDate };
}

export function computeDefectMarkerDate(defect) {
  const dueDateRaw = defect?.due_date;
  if (!dueDateRaw) return null;
  const d = startOfDay(dueDateRaw);
  return Number.isNaN(d.getTime()) ? null : d;
}
